import SignupInviteView from "@/features/auth/components/SignupInviteView";
import { authMethodSearchSchema, type AuthMethodSearch } from "@/features/auth/utils/authMethodSearchParam";
import type { OrganisationSummary, TemplateSettingsSchemaT, UserMetadata } from "@patient-watch/types";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import Spinner from "@patient-watch/ui/spinner";
import { useApplyProgrammeLocaleFromSearch } from "@/features/i18n/useApplyProgrammeLocaleFromSearch";
import { useAuthCopy } from "@/features/i18n/authCopy";

export interface SignupSearchWithMethod extends UserMetadata {
    method?: AuthMethodSearch;
}

function stringParam(search: Record<string, unknown>, key: string): string | undefined {
    const v = search[key];
    return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** Router may deserialise JSON query values to arrays; auth metadata must stay JSON strings for quickstart_data */
function jsonArrayStringParam(search: Record<string, unknown>, key: string): string | undefined {
    const v = search[key];
    if (typeof v === "string" && v.trim().length > 0) return v;
    if (Array.isArray(v)) {
        try {
            return JSON.stringify(v);
        } catch {
            return undefined;
        }
    }
    return undefined;
}

function nonEmptyString(v: unknown): string | undefined {
    return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

function booleanParam(search: Record<string, unknown>, key: string): boolean | undefined {
    const v = search[key];
    if (v === true || v === "true") return true;
    if (v === false || v === "false") return false;
    return undefined;
}

function referenceDateModeParam(
    search: Record<string, unknown>,
    key: string
): "diary_start" | "ask_patient" | undefined {
    const v = search[key];
    return v === "diary_start" || v === "ask_patient" ? v : undefined;
}

export const validateSignupSearch = (search: Record<string, unknown>): SignupSearchWithMethod => {
    const methodParsed = authMethodSearchSchema.safeParse(search?.method);
    const method: AuthMethodSearch | undefined = methodParsed.success ? methodParsed.data : undefined;
    const parseOrganisation = (value: unknown): Partial<OrganisationSummary> | undefined => {
        if (!value) return undefined;
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value) as Record<string, unknown>;
                return parsed as Partial<OrganisationSummary>;
            } catch {
                return undefined;
            }
        }
        if (typeof value === "object") {
            return value as Partial<OrganisationSummary>;
        }
        return undefined;
    };

    const organisationFromObject = parseOrganisation(search?.organisation);
    const rawOrganisationId =
        nonEmptyString(organisationFromObject?.organisation_id) ?? nonEmptyString(search?.organisation_id);
    const rawOrganisationName =
        nonEmptyString(organisationFromObject?.organisation_name) ?? nonEmptyString(search?.organisation_name);

    const organisationId = rawOrganisationId;
    const organisationName = rawOrganisationName;

    const organisation = organisationId
        ? {
              organisation_id: organisationId,
              organisation_name: organisationName ?? "",
          }
        : undefined;

    return {
        method,
        role: search?.role as "PATIENT",
        email: (search?.email as string) ?? undefined,
        phone: stringParam(search, "phone"),
        organisation: organisation,
        ...(organisationId
            ? {
                  organisation_id: organisationId,
                  organisation_name: organisationName ?? "",
              }
            : {}),
        quickstart: (search?.quickstart as TemplateSettingsSchemaT) ?? undefined,
        invited_by: stringParam(search, "invited_by"),
        injecting_clinician_id: stringParam(search, "injecting_clinician_id"),
        referring_clinician_id: stringParam(search, "referring_clinician_id"),
        operating_clinician_id: stringParam(search, "operating_clinician_id"),
        shockwaving_clinician_id: stringParam(search, "shockwaving_clinician_id"),
        educated_by: stringParam(search, "educated_by"),
        contact_id: stringParam(search, "contact_id"),
        name: stringParam(search, "name"),
        description: stringParam(search, "description"),
        trackers: jsonArrayStringParam(search, "trackers"),
        diary_labels: jsonArrayStringParam(search, "diary_labels"),
        required_events: jsonArrayStringParam(search, "required_events"),
        // Template consent selections must survive the QR/self-signup link so the
        // quickstart webhook can freeze them onto the diary (patient + clinician).
        consents: jsonArrayStringParam(search, "consents"),
        orthopaedic_area: stringParam(search, "orthopaedic_area"),
        surgery_option_id: stringParam(search, "surgery_option_id"),
        quickstart_id: stringParam(search, "quickstart_id"),
        quickstart_key: stringParam(search, "quickstart_key"),
        study_invite_id: stringParam(search, "study_invite_id"),
        diary_invite_id: stringParam(search, "diary_invite_id"),
        specialty: stringParam(search, "specialty"),
        hospital_number: stringParam(search, "hospital_number"),
        injection_details: jsonArrayStringParam(search, "injection_details"),
        injection_method_id: stringParam(search, "injection_method_id"),
        injection_id: stringParam(search, "injection_id"),
        sequential_forms: booleanParam(search, "sequential_forms"),
        require_hospital_number: booleanParam(search, "require_hospital_number"),
        reference_date_mode: referenceDateModeParam(search, "reference_date_mode"),
        locale: stringParam(search, "locale"),
        new_diary_reminder_sequence_id: stringParam(search, "new_diary_reminder_sequence_id"),
        entry_reminder_sequence_id: stringParam(search, "entry_reminder_sequence_id"),
    };
};

function SignupLoading() {
    const copy = useAuthCopy();
    return (
        <div className="flex flex-col justify-center items-center gap-4 md:py-6">
            <Spinner />
            <p className="text-muted-foreground text-sm">{copy.loadingSignup}</p>
        </div>
    );
}

const Signup = () => {
    const search = Route.useSearch();
    useApplyProgrammeLocaleFromSearch(search.locale);

    return (
        <Suspense fallback={<SignupLoading />}>
            <SignupInviteView search={search} />
        </Suspense>
    );
};

export const Route = createFileRoute("/_auth/_public/signup/")({
    component: Signup,
    validateSearch: validateSignupSearch,
});
